import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AiCreditPurchaseStatus } from '@scoutaround/script-breakdown-shared';

import { api } from '../../lib/api.js';
import { Callout } from '../Callout.js';
import { creditAmount } from './format.js';
import { forgetCreditPurchase } from './sessionMemory.js';
import { refreshCredits } from './useProjectCredits.js';

/** How often the purchase is asked about while its payment is being confirmed. */
export const PURCHASE_POLL_MS = 2_000;

/**
 * A FAILED STATUS RIGHT AFTER A CONFIRMED CARD IS NOT YET FINAL.
 *
 * A declined first card marks the purchase FAILED, and the same PaymentIntent
 * stays payable: a second card on the same page can still succeed, and its
 * webhook lands a moment after Stripe's answer reaches the browser. Saying
 * "the payment did not go through" in that window is false and sends the
 * producer to pay again (the digital order page's lesson, #1761). So for this
 * long after the card step a FAILED answer is rechecked quietly.
 */
export const FAILED_GRACE_MS = 20_000;

/** Past this, a payment still unconfirmed is said to be slow — never "failed". */
export const SLOW_AFTER_MS = 60_000;

/** States after which the purchase will not change again on its own. */
const FINAL: ReadonlySet<AiCreditPurchaseStatus> = new Set(['PAID', 'CANCELLED', 'REFUNDED', 'PARTIALLY_REFUNDED']);

/**
 * "CONFIRMING YOUR PAYMENT" — until the purchase itself says what happened.
 *
 * Stripe accepting a card, or the browser coming back from a redirect, proves
 * nothing (design B.4): credits are issued only by the signature-verified
 * webhook. This polls `GET …/credits/purchases/:uuid` until the purchase is
 * PAID, FAILED or otherwise final, and only then says so. Once PAID it
 * refreshes the wallet and the history, which is what makes the new balance
 * appear on every surface without a reload.
 */
export function CreditPurchaseConfirmation({
  projectId,
  purchaseUuid,
  onPaid,
  onRetry,
  onDone,
}: {
  projectId: string;
  purchaseUuid: string;
  /** Once, when the purchase is PAID and the wallet has been refreshed. */
  onPaid?: () => void;
  /** Back to the packages, for another attempt. */
  onRetry: () => void;
  /** Close the purchase. */
  onDone?: () => void;
}) {
  const queryClient = useQueryClient();
  const [graceOver, setGraceOver] = useState(false);
  const [slow, setSlow] = useState(false);

  const purchase = useQuery({
    queryKey: ['ai-credit-purchase', projectId, purchaseUuid],
    queryFn: () => api.creditPurchase(projectId, purchaseUuid),
    // Its own question each time: a cached answer from before the card step
    // would say PENDING about a purchase that has since been paid.
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status !== undefined && FINAL.has(status)) return false;
      if (status === 'FAILED' && graceOver) return false;
      if (slow) return false;

      return PURCHASE_POLL_MS;
    },
  });

  useEffect(() => {
    const grace = setTimeout(() => {
      setGraceOver(true);
    }, FAILED_GRACE_MS);
    const bound = setTimeout(() => {
      setSlow(true);
    }, SLOW_AFTER_MS);

    return () => {
      clearTimeout(grace);
      clearTimeout(bound);
    };
  }, []);

  const status = purchase.data?.status;
  const settledFailed = status === 'FAILED' && graceOver;

  // Forget the remembered purchase once its outcome is final, and on PAID
  // refresh what it changed — once, however many renders see it.
  const announced = useRef(false);
  useEffect(() => {
    if (status === undefined) return;
    if (!FINAL.has(status) && !settledFailed) return;

    forgetCreditPurchase(projectId, purchaseUuid);

    if (status === 'PAID' && !announced.current) {
      announced.current = true;
      void refreshCredits(queryClient, projectId).then(() => {
        onPaid?.();
      });
    }
  }, [status, settledFailed, projectId, purchaseUuid, queryClient, onPaid]);

  if (status === 'PAID') {
    return (
      <div data-testid="credit-purchase-paid" data-status="PAID">
        <Callout
          tone="ok"
          title={`${creditAmount(purchase.data?.creditsCents ?? 0)} of credits added`}
          testId="credit-purchase-paid-callout"
          action={
            onDone === undefined ? undefined : (
              <button type="button" className="ctl" data-testid="credit-purchase-done" onClick={onDone}>
                Done
              </button>
            )
          }
        >
          Payment received. The credits are on this production now and can be spent on AI straight
          away.
        </Callout>
      </div>
    );
  }

  if (settledFailed || status === 'CANCELLED') {
    return (
      <div data-testid={status === 'CANCELLED' ? 'credit-purchase-cancelled' : 'credit-purchase-failed'} data-status={status}>
        <Callout
          tone="warn"
          title={status === 'CANCELLED' ? 'This purchase was cancelled' : 'The payment did not go through'}
          testId="credit-purchase-outcome"
          action={
            <button type="button" className="ctl" data-testid="credit-purchase-retry" onClick={onRetry}>
              Try again
            </button>
          }
        >
          No credits were added and nothing was charged. You can try again with the same or another
          card.
        </Callout>
      </div>
    );
  }

  if (status === 'REFUNDED' || status === 'PARTIALLY_REFUNDED') {
    return (
      <div data-testid="credit-purchase-refunded" data-status={status}>
        <Callout tone="info" title="This purchase was refunded" testId="credit-purchase-outcome">
          The credit history on Plan &amp; billing shows what was refunded.
        </Callout>
      </div>
    );
  }

  if (status === 'PROCESSING') {
    return (
      <div data-testid="credit-purchase-processing" data-status="PROCESSING">
        <Callout tone="info" title="Your bank is still processing the payment" testId="credit-purchase-outcome">
          The credits are added as soon as the payment clears. You can leave this page — there is no
          need to pay again.
        </Callout>
      </div>
    );
  }

  if (purchase.isError && purchase.data === undefined) {
    return (
      <div data-testid="credit-purchase-unknown">
        <Callout
          tone="info"
          title="Could not check the payment yet"
          testId="credit-purchase-outcome"
          action={
            <button
              type="button"
              className="ctl"
              data-testid="credit-purchase-recheck"
              onClick={() => {
                void purchase.refetch();
              }}
            >
              Check again
            </button>
          }
        >
          This is a problem reaching the server, not with the payment. Do not pay again — check again in
          a moment.
        </Callout>
      </div>
    );
  }

  if (slow) {
    // NOT AN ERROR, AND IT MUST NOT READ AS ONE: a card may well have been
    // charged, and what has not happened yet is on our side.
    return (
      <div data-testid="credit-purchase-slow" data-status={status ?? 'UNKNOWN'}>
        <Callout
          tone="info"
          title="Your payment is taking longer than usual to confirm"
          testId="credit-purchase-outcome"
          action={
            <button
              type="button"
              className="ctl"
              data-testid="credit-purchase-recheck"
              onClick={() => {
                setSlow(false);
                void purchase.refetch();
              }}
            >
              Check again
            </button>
          }
        >
          Do not pay again. The credits are added as soon as the payment is confirmed, and the credit
          history on Plan &amp; billing will show them.
        </Callout>
      </div>
    );
  }

  return (
    <div className="credits-confirming" data-testid="credit-purchase-confirming" data-status={status ?? 'UNKNOWN'} aria-busy="true">
      <p className="colh" style={{ margin: 0 }}>
        Confirming your payment
      </p>
      <div className="sa-sweep-track" aria-hidden="true" style={{ marginTop: 10 }}>
        <span className="sa-sweep-bar" />
      </div>
      <p className="mm" style={{ fontSize: 12.5, margin: '8px 0 0' }} role="status" aria-live="polite">
        This takes a few seconds. The credits are added once the payment is confirmed — there is no need
        to pay again.
      </p>
    </div>
  );
}
